/* eslint-disable */
import React, { useRef, useEffect, useState } from "react";
import isString from "lodash-es/isString";
import isArray from "lodash-es/isArray";
import get from "lodash-es/get";
import map from "lodash-es/map";
import PropTypes from "prop-types";
import cloneDeep from "lodash-es/cloneDeep";
import find from "lodash-es/find";
import take from "lodash-es/take";
import { isFunction, isObject, isEmpty, hasProperty } from "./utils";
import Tooltip from "../components/tooltip/tooltip";
import Icon from "../components/icon/icon";

export const showMaxFilterValues = 3;

export const ButtonWrapper = ({
  appliedFilters,
  onButtonMouseDown,
  tooltipGroups,
  className,
  ariaLabel,
  dataTestId,
  buttonRef
}) => {
  let prevValue = useRef(null);
  const [filterCountChange, setFilterCountChange] = useState(false);
  const appliedFiltersCount = getAppliedFiltersCount(
    appliedFilters,
    tooltipGroups
  );
  useEffect(() => {
    if (prevValue.current !== appliedFiltersCount) {
      prevValue.current = appliedFiltersCount;
      setFilterCountChange(true);
    }

    const timer = setTimeout(() => {
      if (filterCountChange) {
        setFilterCountChange(false);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [appliedFiltersCount, filterCountChange]);
  return (
    <button
      data-testid={dataTestId}
      filtercount={appliedFiltersCount}
      onMouseDown={onButtonMouseDown}
      aria-label={ariaLabel}
      className={`oui-btn oui-btn-md oui-btn-outline-primary oui-filter-button-trigger ${
        (!!appliedFiltersCount && "oui-filters-applied") || ""
      } ${className || ""} ${filterCountChange ? "oui-filter-count-anim" : ""}`}
      ref={buttonRef}
    >
      <Icon
        className="icon"
        svg={
          <svg
            id="filter-button-svg"
            className="filter-button-svg"
            data-name="Layer 2"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 29"
          >
            <path
              d="M670-94h28a2,2,0,0,0,2-2,2,2,0,0,0-2-2H670a2,2,0,0,0-2,2A2,2,0,0,0,670-94Z"
              transform="translate(-668 98)"
            />
            <path
              d="M694-86H674a2,2,0,0,0-2,2,2,2,0,0,0,2,2h20a2,2,0,0,0,2-2A2,2,0,0,0,694-86Z"
              transform="translate(-668 98)"
            />
            <path
              d="M690-74H678a2,2,0,0,0-2,2,2,2,0,0,0,2,2h12a2,2,0,0,0,2-2A2,2,0,0,0,690-74Z"
              transform="translate(-668 98)"
            />
          </svg>
        }
      />
    </button>
  );
};

export const RenderToolTip = ({ appliedFilters, tooltipGroups }) => {
  let tooltip;

  if (tooltipGroups) {
    tooltip = createGroupedTooltip(appliedFilters, tooltipGroups);
  } else {
    tooltip = map(
      appliedFilters,
      ({ title, value, valueRenderer = null }, key) => {
        if (!isEmpty(value) && title) {
          return (
            <div className="oui-filter" key={key}>
              <div className="oui-filter-title" key={title}>
                {title}
              </div>
              {renderFilter(value, valueRenderer)}
            </div>
          );
        } else {
          return "";
        }
      }
    );
  }

  return (
    <div className="oui-filter-tooltip">
      <div className="oui-tool-tip-title" key="key-tool-tip-title">
        {" "}
        {"Filters Applied"}
      </div>
      {tooltip}
    </div>
  );
};

RenderToolTip.defaultProps = {
  appliedFilters: []
};

RenderToolTip.propTypes = {
  appliedFilters: PropTypes.array
};

// render single value
export const renderRow = (
  value,
  key,
  className = "oui-filter-value",
  valueRenderer
) => (
  <div className={`${className}`} key={key}>
    {isFunction(valueRenderer) ? valueRenderer(value || "") : String(value)}
  </div>
);

export const renderWithMoreFilterCount = (
  filters,
  text,
  key,
  showMaxFilterValues,
  renderer
) => {
  if (filters.length > showMaxFilterValues && key === showMaxFilterValues - 1) {
    const moreFilterCount = filters.length - showMaxFilterValues;
    const moreFilterText = `+${moreFilterCount}`;
    return renderRow(`${text}  ${moreFilterText}`, key, "", renderer);
  } else {
    return renderRow(text, key, "", renderer);
  }
};

export const isTagSelectValue = value => {
  if (!isArray(value)) {
    return false;
  }

  return value.some(item => {
    return (
      (hasProperty(item, "key") &&
        hasProperty(item.key, "label") &&
        hasProperty(item, "value") &&
        hasProperty(item.value, "label")) ||
      (Object.keys(item).length === 1 && hasProperty(item, "value"))
    );
  });
};

export const tagSelectValueRenderer = (item, key, renderer, moreFilterText) => {
  return (
    <div className="oui-filter-value" key={key}>
      {isFunction(renderer) ? (
        renderer(item)
      ) : !item.key && item.value ? (
        item.value
      ) : (
        <React.Fragment>
          {item.key.label}: {item.value.label}
        </React.Fragment>
      )}
      {moreFilterText ? ` ${moreFilterText}` : null}
    </div>
  );
};

export const renderTagSelectValue = (
  filters,
  item,
  key,
  showMaxFilterValues,
  renderer
) => {
  if (filters.length > showMaxFilterValues && key === showMaxFilterValues - 1) {
    const moreFilterCount = filters.length - showMaxFilterValues;
    const moreFilterText = `+${moreFilterCount}`;
    return tagSelectValueRenderer(item, key, renderer, moreFilterText);
  } else {
    return tagSelectValueRenderer(item, key, renderer);
  }
};

// Identify filter value type and render
export const renderFilter = (value = "", valueRenderer) => {
  const renderer = isFunction(valueRenderer)
    ? valueRenderer
    : v => (isString(v) ? v : String(v));

  if (isTagSelectValue(value)) {
    return map(take(value, showMaxFilterValues), (valueObject, key) => {
      return renderTagSelectValue(
        value,
        valueObject,
        key,
        showMaxFilterValues,
        valueRenderer
      );
    });
  } else if (
    !isEmpty(value) &&
    isArray(value) &&
    isObject(value[0]) &&
    value[0].label
  ) {
    // Render array of objects
    return map(take(value, showMaxFilterValues), (valueObject, key) => {
      return renderWithMoreFilterCount(
        value,
        valueObject.label,
        key,
        showMaxFilterValues,
        renderer
      );
    });
  } else if (!isEmpty(value) && isArray(value) && isString(value[0])) {
    // Render array of strings
    return map(take(value, showMaxFilterValues), (valueString, key) => {
      return renderWithMoreFilterCount(
        value,
        valueString,
        key,
        showMaxFilterValues,
        renderer
      );
    });
  } else if (isObject(value) && value.label) {
    // Render single object
    return renderRow(value.label, "objectKey", "", renderer);
  }

  return renderRow(value, "value", "", renderer);
};

export const combineFilters = (
  { appliedFilters = [], filters = [] } = {},
  state
) => {
  let oldFilters = cloneDeep(get(state, "appliedFilters", []));
  let newFilters = [];
  filters.forEach(element => {
    const { fieldName } = element;
    // check if filter already present in state.appliedFilter
    const item = find(oldFilters, {
      fieldName
    });
    // filter is not preset
    if (isEmpty(item)) {
      // filter has applied value
      const appliedFilterItem = find(appliedFilters, {
        fieldName
      });
      isEmpty(appliedFilterItem)
        ? newFilters.push(element)
        : newFilters.push({ ...element, value: appliedFilterItem.value });
    } else {
      // if filter is present
      const oldFilterItem = find(oldFilters, {
        fieldName
      });
      newFilters.push(oldFilterItem);
    }
  });

  return newFilters;
};

/* convert object 
    FROM : [{ fieldName: 'userName', value: 'someValue' }]
    TO   : { userName: 'someValue' } */
export const popoverDataParser = appliedFilters => {
  let obj = {};
  appliedFilters.forEach(filter => {
    obj[filter.fieldName] = get(filter, "value", "");
  });
  return obj;
};

export const getAppliedFiltersCount = (appliedFilters = [], tooltipGroups) => {
  if (tooltipGroups) {
    return getTooltipGroupAppliedCount(appliedFilters, tooltipGroups);
  }

  return appliedFilters.filter(({ value }) => !isEmpty(value)).length;
};

export const ToolTipWrapper = ({
  appliedFilters,
  onButtonMouseDown,
  togglePopOver,
  tooltipGroups,
  className,
  ariaLabel,
  dataTestId,
  buttonRef
}) => {
  const appliedFiltersCount = getAppliedFiltersCount(
    appliedFilters,
    tooltipGroups
  );
  return (
    <Tooltip
      text={() => (
        <RenderToolTip
          appliedFilters={appliedFilters}
          tooltipGroups={tooltipGroups}
        />
      )}
      placement="bottom"
      showToolTip={!togglePopOver && !!appliedFiltersCount}
      className="oui-filter-tooltip"
    >
      <ButtonWrapper
        appliedFilters={appliedFilters}
        onButtonMouseDown={onButtonMouseDown}
        tooltipGroups={tooltipGroups}
        className={className}
        ariaLabel={ariaLabel}
        dataTestId={dataTestId}
        buttonRef={buttonRef}
      />
    </Tooltip>
  );
};

export const createGroupedTooltip = (appliedFilters, tooltipGroups) => {
  const tooltipRows = map(
    appliedFilters,
    ({ fieldName, title, value, valueRenderer = null }, key) => {
      if (
        !isFieldInTooltipGroups(tooltipGroups, fieldName) &&
        !isEmpty(value) &&
        title
      ) {
        return (
          <div className="oui-filter" key={key}>
            <div className="oui-filter-title" key={title}>
              {title}
            </div>
            {renderFilter(value, valueRenderer)}
          </div>
        );
      }
    }
  );

  const groupedFields = tooltipGroups
    .map(group => {
      if (isFunction(group.groupRenderer)) {
        const fields = createGroupFieldValuesObject(
          group.fields,
          appliedFilters
        );
        const fieldValues = Object.values(fields);
        const notEmptyFields = fieldValues.filter(f => f.value);
        if (notEmptyFields.length > 0) {
          const groupRenderValue = group.groupRenderer(fields);
          return groupRenderValue ? (
            <div className="oui-filter" key={group.groupName}>
              <div className="oui-filter-title" key={group.groupName}>
                {group.groupName}
              </div>
              <div className="oui-filter-value">{groupRenderValue}</div>
            </div>
          ) : (
            ""
          );
        }

        return "";
      }
    })
    .filter(Boolean);

  return tooltipRows.concat(groupedFields);
};

export const createGroupFieldValuesObject = (fields, appliedFilters) => {
  const fieldValues = {};

  fields.forEach(fieldName => {
    fieldValues[fieldName] = getValueOfAppliedField(fieldName, appliedFilters);
  });

  return fieldValues;
};

export const getValueOfAppliedField = (fieldName, appliedFilters) => {
  let value = null;
  let remainingCount = 0;
  let valueRenderer = v => v;

  for (const filter of appliedFilters) {
    if (filter.fieldName === fieldName) {
      value = filter.value;
      if (isFunction(filter.valueRenderer)) {
        valueRenderer = filter.valueRenderer;
      }
      break;
    }
  }

  if (isArray(value) && value && isObject(value[0])) {
    remainingCount = value.length - showMaxFilterValues;
    value = map(take(value, showMaxFilterValues), valueObject => {
      return valueObject;
    });
  } else if (isArray(value) && value && isString(value[0])) {
    remainingCount = value.length - showMaxFilterValues;
    value = map(take(value, showMaxFilterValues), valueString => {
      return valueString;
    });
  } else if (isObject(value) && value.label) {
    value = value.label;
  }

  return {
    value: valueRenderer(value),
    remainingCount: Math.max(remainingCount, 0)
  };
};

export const isFieldInTooltipGroups = (tooltipGroups, fieldName) => {
  if (tooltipGroups && fieldName) {
    for (const group of tooltipGroups) {
      if (group.fields && group.fields.includes(fieldName)) {
        return group;
      }
    }
  }

  return false;
};

export const makeFieldNameToGroupMap = tooltipGroups => {
  const map = {};

  if (tooltipGroups && Array.isArray(tooltipGroups)) {
    tooltipGroups.forEach(group => {
      if (group.fields && Array.isArray(group.fields)) {
        group.fields.forEach(field => {
          map[field] = group.groupName;
        });
      }
    });
  }

  return map;
};

export const getTooltipGroupAppliedCount = (appliedFilters, tooltipGroups) => {
  const fieldToGroup = makeFieldNameToGroupMap(tooltipGroups);
  const consumedGroups = [];
  let count = 0;

  appliedFilters.forEach(({ value, fieldName }) => {
    if (!isEmpty(value)) {
      if (isFieldInTooltipGroups(tooltipGroups, fieldName)) {
        if (!consumedGroups.includes(fieldToGroup[fieldName])) {
          count += 1;
          consumedGroups.push(fieldToGroup[fieldName]);
        }
      } else {
        count += 1;
      }
    }
  });
  return count;
};
